import type { TFunction } from 'i18next'
import { formatKstDateTime } from '../../utils/formatKstDateTime'

/**
 * Human-readable labels for fulfillment orders and orders.
 *
 * Raw UUIDs are meaningless to dealers, so screens show
 *   - title/list:  `닉네임 (YYYY-MM-DD HH:mm)`
 *   - context:     `YYYY-MM-DD HH:mm:ss (방송명 | 마켓플레이스)` — second precision keeps it unique
 * UUIDs stay in URLs, test ids and API calls only.
 */

interface FoLike {
  createdAt: string
  buyer: { nickname: string }
  broadcast?: { title: string } | null
}

interface OrderLike {
  createdAt: string
  orderItems: Array<{ productName: string }>
  broadcast?: { title: string } | null
}

export const formatDateTime = (iso: string): string => formatKstDateTime(iso, 'YYYY-MM-DD HH:mm')
export const formatDateTimeSeconds = (iso: string): string =>
  formatKstDateTime(iso, 'YYYY-MM-DD HH:mm:ss')

const channelLabel = (broadcast: { title: string } | null | undefined, t: TFunction): string =>
  broadcast?.title ?? t('common.status.marketplace')

/** `닉네임 (YYYY-MM-DD HH:mm)` */
export const formatFoTitle = (fo: FoLike): string =>
  `${fo.buyer.nickname} (${formatDateTime(fo.createdAt)})`

/** `YYYY-MM-DD HH:mm:ss (방송명)` */
export const formatFoContext = (fo: FoLike, t: TFunction): string =>
  `${formatDateTimeSeconds(fo.createdAt)} (${channelLabel(fo.broadcast, t)})`

/** `상품명 · YYYY-MM-DD HH:mm:ss` — first item name; multi-item orders get `외 N건` */
export const formatOrderLabel = (order: OrderLike): string => {
  const first = order.orderItems[0]?.productName ?? '-'
  const rest = order.orderItems.length > 1 ? ` 외 ${order.orderItems.length - 1}건` : ''
  return `${first}${rest} · ${formatDateTimeSeconds(order.createdAt)}`
}

interface OrderBriefLike {
  createdAt?: string | null
  productName?: string | null
  broadcastTitle?: string | null
}

/**
 * `상품명 · YYYY-MM-DD HH:mm:ss (방송명)` for order briefs (cancellation/return/settlement rows).
 * Falls back gracefully when the API has not enriched the brief.
 */
export const formatOrderBriefLabel = (order: OrderBriefLike): string => {
  const product = order.productName ?? '-'
  const when = order.createdAt ? ` · ${formatDateTimeSeconds(order.createdAt)}` : ''
  const channel = order.broadcastTitle ? ` (${order.broadcastTitle})` : ''
  return `${product}${when}${channel}`
}
